import { DragEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ConfirmDialog from '../ConfirmDialog'
import ClipPreview, { type ClipPreviewHandle, type ExportView, type PanelOptions } from './ClipPreview'
import {
  clipCaptionsUrl,
  clipFactoryAPI,
  clipFactoryErrorText,
  clipFactoryUrl,
  signedUrl,
  uploadFile,
  type CallMatches,
  type Clip,
  type ClipFactoryConfig,
  type ExportOptions,
  type ClipOverlay,
  type ExportRecord,
  type LinkToken,
  type PostCopy,
  type ProcessStatus,
  type UploadProgress,
  type VideoResults,
  type CallClips,
  type VideoSummary,
} from './clipFactoryApi'
import {
  clipKey,
  estimateProcessingSeconds,
  exportSig,
  formatBytes,
  formatEstimate,
  formatTime,
  recordSig,
  type Range,
  type Word,
} from './clipUtils'
import ClipHistoryPanel, { edgesSentence } from './ClipHistoryPanel'
import CreateVideo from './CreateVideo'
import EpisodePackage from './EpisodePackage'
import FixPanel from './FixPanel'
import FindClips from './FindClips'
import RecordDialog from './RecordDialog'
import ScoreRing from './ScoreRing'
import TrimEditor from './TrimEditor'

const POLL_MS = 1500
const TRIM_SAVE_DELAY_MS = 500
// Renew the media link this long before it expires.
const TOKEN_RENEW_EARLY_S = 300
// Give up reading a chosen file's length after this long (the estimate is optional).
const DURATION_READ_MS = 10_000

const ACCEPT = '.mp4,.mov,.m4v,.mkv,.webm,.avi,video/*'

const DEFAULT_OPTIONS: PanelOptions = {
  format: 'vertical_speaker',
  captions: true,
  captionStyle: 'classic',
  chart: false,
  watermark: true,
  introOutro: false,
  hookOn: true,
}

const formatDate = (unixSeconds: number) =>
  new Date(unixSeconds * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

const tutorialUrl = (): string | null => (process.env.REACT_APP_CLIPS_TUTORIAL_URL || '').trim() || null

/** A video file's length from its metadata, or null if the browser can't tell. */
const readDuration = (file: File): Promise<number | null> =>
  new Promise((resolve) => {
    if (typeof URL.createObjectURL !== 'function') return resolve(null)
    const url = URL.createObjectURL(file)
    const el = document.createElement('video')
    const done = (value: number | null) => {
      clearTimeout(timer)
      URL.revokeObjectURL(url)
      resolve(value)
    }
    const timer = setTimeout(() => done(null), DURATION_READ_MS)
    el.preload = 'metadata'
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? el.duration : null)
    el.onerror = () => done(null)
    el.src = url
  })

/** Start a download of `url` without leaving the page. */
const triggerDownload = (url: string) => {
  const a = document.createElement('a')
  a.href = url
  a.download = ''
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

interface UploadState {
  filename: string
  progress: UploadProgress
}

/** What a clip's score means, for the picker that gave it. */
const scoreMeaning = (picker?: 'claude' | 'heuristic') =>
  picker === 'heuristic'
    ? 'Scores come from simple rules (a hook, a steady pace, numbers, a clean ending, about 35 seconds long). They rank these clips against each other; they are not a forecast of views and do not compare with AI scores.'
    : 'Scores rank these clips against each other within this video. 100 is the strongest moment here, not a forecast of views.'

const Step = ({ label, done, active }: { label: string; done: boolean; active: boolean }) => (
  <span className={`text-sm ${done ? 'font-medium text-success-600' : active ? 'font-medium text-primary-600' : 'text-gray-400'}`}>
    {label} {done ? '✓' : active ? '…' : ''}
  </span>
)

const HOW_IT_WORKS = [
  { title: 'Upload or record', text: 'Drop in a long video, or record your webcam or screen here.' },
  { title: 'Clips are picked', text: 'The talk is transcribed and the strongest moments are suggested.' },
  { title: 'Export and post', text: 'Trim, add captions and branding, then download or send to Library.' },
]

const modeClass = (active: boolean) =>
  `flex-1 rounded-md px-3 py-1.5 text-sm font-medium sm:flex-none ${active ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`

/**
 * Admin only: upload (or record) a long video and get short clips from it,
 * through the Clip Factory service (REACT_APP_CLIP_FACTORY_URL). Upload →
 * transcript → suggested clips → trim → MP4 export and post copy. "Create a
 * video" makes a video from a locked call or a short script instead.
 */
const ClipsTab = () => {
  const configured = !!clipFactoryUrl()
  const [params, setParams] = useSearchParams()
  const videoId = params.get('video')
  // "Clip it" on a closed call's Moment: ?call=<id> (and, once found, &clip=<key>).
  const callParam = params.get('call')
  const clipParam = params.get('clip')
  const [callClips, setCallClips] = useState<CallClips | null>(null)
  const appliedCall = useRef<string | null>(null)

  const [mode, setMode] = useState<'clips' | 'create'>('clips')
  const [config, setConfig] = useState<ClipFactoryConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [videos, setVideos] = useState<VideoSummary[] | null>(null)
  const [upload, setUpload] = useState<UploadState | null>(null)
  const [recording, setRecording] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [status, setStatus] = useState<ProcessStatus | null>(null)
  const [results, setResults] = useState<VideoResults | null>(null)
  const [token, setToken] = useState<LinkToken | null>(null)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [trimmingKey, setTrimmingKey] = useState<string | null>(null)
  const [trims, setTrims] = useState<Record<string, Range>>({})
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set())
  // What the selected clip's export would draw over it, for the live preview.
  const [overlay, setOverlay] = useState<ClipOverlay | null>(null)
  // Timed words around the clip being trimmed, for snapping and cutting.
  const [words, setWords] = useState<Record<string, Word[]>>({})
  const [options, setOptions] = useState<PanelOptions>(DEFAULT_OPTIONS)
  const [hookTexts, setHookTexts] = useState<Record<string, string>>({})
  const [matches, setMatches] = useState<CallMatches | null>(null)
  const [confirmedCalls, setConfirmedCalls] = useState<Record<string, number | null>>({})
  const [receipts, setReceipts] = useState<Record<string, boolean>>({})
  // The last export started for each clip in this visit, and the settings it was made with.
  const [lastExports, setLastExports] = useState<Record<string, { sig: string; record: ExportRecord }>>({})
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set())
  // Estimated processing seconds, by video id (and for the upload in progress).
  const [estimates, setEstimates] = useState<Record<string, number>>({})
  const [uploadEstimate, setUploadEstimate] = useState<number | null>(null)
  const [deleting, setDeleting] = useState<VideoSummary | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  const preview = useRef<ClipPreviewHandle>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const trimTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const uploadSignal = useRef({ cancelled: false })
  const tokenRef = useRef<LinkToken | null>(null)
  tokenRef.current = token
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      uploadSignal.current.cancelled = true
    }
  }, [])

  const openVideo = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(params)
      if (id) next.set('video', id)
      else next.delete('video')
      setParams(next)
    },
    [params, setParams]
  )

  const loadVideos = useCallback(async () => {
    try {
      setVideos(await clipFactoryAPI.myVideos())
    } catch (e) {
      setError(clipFactoryErrorText(e, "Couldn't load your videos."))
    }
  }, [])

  useEffect(() => {
    if (!configured) return
    loadVideos()
    clipFactoryAPI
      .config()
      .then((c) => mounted.current && setConfig(c))
      .catch(() => undefined) // optional extras (Claude drafts, voice) stay hidden
  }, [configured, loadVideos])

  const created = results?.source?.kind === 'created'

  // Find the clips that match the call; open the newest, or make a video of
  // the call's result when none of your videos mentions it.
  useEffect(() => {
    setCallClips(null)
    if (!configured || !callParam) return
    let stopped = false
    clipFactoryAPI
      .clipsForCall(Number(callParam))
      .then((r) => {
        if (stopped) return
        setCallClips(r)
        const first = r.clips[0]
        if (!first) {
          setMode('create')
        } else if (!videoId) {
          const next = new URLSearchParams(params)
          next.set('video', first.file_id)
          next.set('clip', first.clip_key)
          setParams(next)
        }
      })
      .catch((e) => !stopped && setError(clipFactoryErrorText(e, "Couldn't find the clips for this call.")))
    return () => {
      stopped = true
    }
    // Only when the call asked for changes; the video it opens is set here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured, callParam])

  // Once the video is open: pick the clip and attach the call's receipt, whatever its result.
  useEffect(() => {
    const want = `${videoId}|${clipParam}|${callParam}`
    if (!results || !callParam || !clipParam || appliedCall.current === want) return
    if (!(results.clips?.clips || []).some((c) => clipKey(c) === clipParam)) return
    appliedCall.current = want
    setSelectedKey(clipParam)
    setConfirmedCalls((prev) => ({ ...prev, [clipParam]: Number(callParam) }))
    setReceipts((prev) => ({ ...prev, [clipParam]: true }))
  }, [results, videoId, clipParam, callParam])

  const closeCall = () => {
    const next = new URLSearchParams(params)
    next.delete('call')
    next.delete('clip')
    setParams(next)
  }

  const loadMatches = useCallback(async (id: string) => {
    try {
      const m = await clipFactoryAPI.callMatches(id)
      if (mounted.current) setMatches(m)
    } catch {
      // No tags without matches; the clips still work.
      if (mounted.current) setMatches(null)
    }
  }, [])

  // Follow the open video until its clips are ready, then load them.
  useEffect(() => {
    setStatus(null)
    setResults(null)
    setToken(null)
    setTrims({})
    setLastExports({})
    setMatches(null)
    setConfirmedCalls({})
    setReceipts({})
    setHookTexts({})
    setSelectedKey(null)
    setTrimmingKey(null)
    setWords({})
    if (!configured || !videoId) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout>
    const poll = async () => {
      try {
        const s = await clipFactoryAPI.status(videoId)
        if (stopped) return
        setStatus(s)
        if (s.status === 'completed') {
          const r = await clipFactoryAPI.results(videoId)
          if (stopped) return
          setResults(r)
          setTrims(r.trims || {})
          setSelectedKey(r.clips?.clips?.[0] ? clipKey(r.clips.clips[0]) : null)
          if (r.source?.kind !== 'created') loadMatches(videoId)
          loadVideos()
          return
        }
        if (s.status === 'failed') {
          loadVideos()
          return
        }
      } catch (e) {
        if (stopped) return
        setError(clipFactoryErrorText(e, "Couldn't load this video."))
        return
      }
      timer = setTimeout(poll, POLL_MS)
    }
    poll()
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [configured, videoId, loadVideos, loadMatches])

  // A signed link for the player and downloads, renewed before it expires.
  const resultsId = results?.file_id
  useEffect(() => {
    if (!resultsId) return
    let timer: ReturnType<typeof setTimeout>
    let stopped = false
    const fetchToken = async () => {
      try {
        const t = await clipFactoryAPI.linkToken(resultsId)
        if (stopped) return
        setToken(t)
        const wait = Math.max(30, t.expires_at - Date.now() / 1000 - TOKEN_RENEW_EARLY_S)
        timer = setTimeout(fetchToken, wait * 1000)
      } catch (e) {
        if (!stopped) setError(clipFactoryErrorText(e, "Couldn't load the video player."))
      }
    }
    fetchToken()
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [resultsId])

  // Warn before leaving mid-upload.
  useEffect(() => {
    if (!upload) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [upload])

  /** Upload a chosen or recorded file. `seconds` is its length when already known (a recording). */
  const startUpload = async (file: File, seconds?: number) => {
    setError(null)
    setMode('clips')
    uploadSignal.current = { cancelled: false }
    setUpload({ filename: file.name, progress: { sent: 0, total: file.size, bytesPerSecond: 0, retrying: false } })
    let estimate: number | null = seconds !== undefined ? estimateProcessingSeconds(seconds) : null
    setUploadEstimate(estimate)
    if (seconds === undefined) {
      readDuration(file).then((d) => {
        if (d === null || !mounted.current) return
        estimate = estimateProcessingSeconds(d)
        setUploadEstimate(estimate)
      })
    }
    try {
      const job = await uploadFile(file, (progress) => mounted.current && setUpload({ filename: file.name, progress }),
        uploadSignal.current)
      if (!mounted.current) return
      setUpload(null)
      if (estimate !== null) setEstimates((prev) => ({ ...prev, [job.file_id]: estimate as number }))
      openVideo(job.file_id)
      loadVideos()
    } catch (e) {
      if (!mounted.current) return
      setUpload(null)
      setError(clipFactoryErrorText(e, 'The upload failed.'))
    }
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file && !upload) startUpload(file)
  }

  const clips: Clip[] = results?.clips?.clips || []
  const duration = results?.transcription?.duration || 0
  const selected = clips.find((c) => clipKey(c) === selectedKey) || null
  const rangeOf = (clip: Clip): Range => trims[clipKey(clip)] || { start: clip.start, end: clip.end }
  const postCopyOf = (clip: Clip) => results?.post_copy?.[clipKey(clip)] || clip.post_copy
  const bestKey = clips.length > 1 ? clipKey(clips.reduce((best, c) => (c.score > best.score ? c : best))) : null
  const matchesOf = (clip: Clip) => matches?.clips?.[clipKey(clip)]?.matches || []
  const hookTextOf = (clip: Clip) => hookTexts[clipKey(clip)] ?? clip.title

  /** What an export of `clip` would be made with right now. */
  const exportOptionsOf = (clip: Clip): ExportOptions => {
    const key = clipKey(clip)
    const callId = confirmedCalls[key] ?? null
    const attach = receipts[key] && callId !== null && matchesOf(clip).some((m) => m.id === callId)
    return {
      format: options.format,
      captions: options.captions,
      caption_style: options.captionStyle,
      chart: options.chart,
      watermark: options.watermark,
      intro_outro: options.introOutro,
      hook: options.hookOn ? hookTextOf(clip).trim() || null : null,
      receipt_call_id: attach ? callId : null,
    }
  }

  const changeTrim = (clip: Clip, range: Range) => {
    if (!results) return
    const key = clipKey(clip)
    setTrims((prev) => ({ ...prev, [key]: range }))
    clearTimeout(trimTimers.current[key])
    trimTimers.current[key] = setTimeout(async () => {
      setSavingKeys((prev) => new Set(prev).add(key))
      try {
        await clipFactoryAPI.saveTrim(results.file_id, key, range.start, range.end, range.cuts || [])
        loadMatches(results.file_id) // the mentions follow the trimmed range
      } catch (e) {
        setError(clipFactoryErrorText(e, "Couldn't save the trim."))
      } finally {
        setSavingKeys((prev) => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
      }
    }, TRIM_SAVE_DELAY_MS)
  }

  // The words around a clip, once, when its trim editor opens (the timeline shows 30 s either side).
  useEffect(() => {
    if (!results || !trimmingKey || words[trimmingKey]) return
    const clip = (results.clips?.clips || []).find((c) => clipKey(c) === trimmingKey)
    if (!clip) return
    const span = (results.transcription?.duration || clip.end) as number
    const start = Math.max(0, clip.start - 60)
    const end = Math.min(span, clip.end + 60, start + 600)
    let stopped = false
    clipFactoryAPI
      .words(results.file_id, start, end)
      .then((r) => !stopped && setWords((prev) => ({ ...prev, [trimmingKey]: r.words })))
      .catch(() => !stopped && setWords((prev) => ({ ...prev, [trimmingKey]: [] }))) // no snapping or cutting by words
    return () => {
      stopped = true
    }
  }, [results, trimmingKey, words])

  const resetTrim = async (clip: Clip) => {
    if (!results) return
    const key = clipKey(clip)
    clearTimeout(trimTimers.current[key])
    setTrims((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    try {
      await clipFactoryAPI.resetTrim(results.file_id, key)
      loadMatches(results.file_id)
    } catch (e) {
      setError(clipFactoryErrorText(e, "Couldn't reset the trim."))
    }
  }

  /** The receipt card's lines for an export of `clip` made now, or null without one. */
  const receiptLinesOf = (clip: Clip): string[] | null => {
    const callId = exportOptionsOf(clip).receipt_call_id
    const call = callId !== null ? matchesOf(clip).find((m) => m.id === callId) : null
    return call ? call.card_lines || [call.card_text] : null
  }

  const exportViewOf = (clip: Clip): ExportView => {
    if (created) {
      const render = results?.exports?.find((x) => x.id === 'render' && x.status === 'completed')
      return render ? { state: 'ready', record: render } : { state: 'idle' }
    }
    const key = clipKey(clip)
    const sig = exportSig(rangeOf(clip), exportOptionsOf(clip))
    const last = lastExports[key]
    if (busyKeys.has(key)) return { state: 'busy', record: last?.record || null }
    if (last) {
      if (last.sig === sig) {
        if (last.record.status === 'completed') return { state: 'ready', record: last.record }
        if (last.record.status === 'failed') return { state: 'failed', record: last.record }
      } else if (last.record.status === 'completed') {
        return { state: 'stale', record: last.record }
      }
    }
    const saved = results?.exports?.find((x) => x.status === 'completed' && recordSig(x) === sig)
    return saved ? { state: 'ready', record: saved } : { state: 'idle' }
  }

  const startExport = async (clip: Clip) => {
    if (!results) return
    const id = results.file_id
    const key = clipKey(clip)
    const range = rangeOf(clip)
    const opts = exportOptionsOf(clip)
    const sig = exportSig(range, opts)
    const show = (record: ExportRecord) => setLastExports((prev) => ({ ...prev, [key]: { sig, record } }))
    setBusyKeys((prev) => new Set(prev).add(key))
    try {
      let record = await clipFactoryAPI.startExport(id, { ...range, ...opts })
      while (mounted.current && (record.status === 'queued' || record.status === 'processing')) {
        show(record)
        await new Promise((r) => setTimeout(r, POLL_MS))
        record = await clipFactoryAPI.exportStatus(id, record.id)
      }
      show(record)
      // Finished: start the download straight away.
      const url = record.status === 'completed' ? signedUrl(id, `exports/${record.id}/download`, tokenRef.current) : null
      if (url && mounted.current) triggerDownload(url)
    } catch (e) {
      setError(clipFactoryErrorText(e, 'The export failed.'))
    } finally {
      setBusyKeys((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  /** After a caption fix: the transcript, titles and post copy changed, and exports
   * with burned-in captions were expired by the server, so they're made again. */
  const captionsChanged = async () => {
    if (!results) return
    const id = results.file_id
    setLastExports({})
    try {
      const r = await clipFactoryAPI.results(id)
      if (mounted.current) setResults(r)
      loadMatches(id)
    } catch (e) {
      setError(clipFactoryErrorText(e, "Couldn't reload this video."))
    }
  }

  const postCopySaved = (clip: Clip, copy: PostCopy) =>
    setResults((prev) => (prev ? { ...prev, post_copy: { ...prev.post_copy, [clipKey(clip)]: copy } } : prev))

  const confirmDelete = async () => {
    if (!deleting) return
    setDeleteBusy(true)
    try {
      await clipFactoryAPI.deleteVideo(deleting.file_id)
      if (deleting.file_id === videoId) openVideo(null)
      setDeleting(null)
      loadVideos()
    } catch (e) {
      setError(clipFactoryErrorText(e, "Couldn't delete the video."))
      setDeleting(null)
    } finally {
      setDeleteBusy(false)
    }
  }

  const previewRange = selected && !created ? rangeOf(selected) : null
  const previewBody = previewRange && results
    ? JSON.stringify({
        start: previewRange.start,
        end: previewRange.end,
        cuts: previewRange.cuts || [],
        format: options.format,
        receipt: !!(selected && receiptLinesOf(selected)),
        chart: options.chart,
        hook: options.hookOn && !!(selected && hookTextOf(selected).trim()),
      })
    : null
  // The overlay follows the clip, its trim and cuts, the shape and the receipt card;
  // the transcript too (captionsChanged reloads results).
  const previewId = results?.file_id
  const previewTranscript = results?.transcription
  useEffect(() => {
    setOverlay(null)
    if (!previewId || !previewBody) return
    let stopped = false
    const timer = setTimeout(() => {
      clipFactoryAPI
        .preview(previewId, JSON.parse(previewBody))
        .then((o) => !stopped && setOverlay(o))
        .catch(() => undefined) // no overlay: the plain video still plays
    }, 300)
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [previewId, previewBody, previewTranscript])

  if (!configured) {
    return (
      <div className="bg-white rounded-lg shadow p-6" data-testid="clips-tab">
        <h3 className="text-xl font-bold text-gray-900">Clips isn't configured</h3>
        <p className="text-sm text-gray-600 mt-1">
          Set REACT_APP_CLIP_FACTORY_URL to the Clip Factory backend's URL and redeploy the frontend.
        </p>
      </div>
    )
  }

  const mediaUrl = results ? signedUrl(results.file_id, 'media', token) : null
  const selectedView: ExportView = selected ? exportViewOf(selected) : { state: 'idle' }
  const viewRecord = selectedView.state === 'ready' ? selectedView.record : null
  const downloadUrl = results && viewRecord ? signedUrl(results.file_id, `exports/${viewRecord.id}/download`, token) : null
  const captionUrlOf = (format: 'srt' | 'vtt') =>
    results && selected ? clipCaptionsUrl(results.file_id, rangeOf(selected), format, options.introOutro, token) : null
  const captionSrt = !created ? captionUrlOf('srt') : null
  const captionVtt = !created ? captionUrlOf('vtt') : null
  const captionUrls = captionSrt && captionVtt ? { srt: captionSrt, vtt: captionVtt } : null

  const uploadPct = upload ? Math.floor((upload.progress.sent / Math.max(upload.progress.total, 1)) * 100) : 0
  const secondsLeft =
    upload && upload.progress.bytesPerSecond > 0
      ? (upload.progress.total - upload.progress.sent) / upload.progress.bytesPerSecond
      : null
  const processing = status && status.status !== 'completed' && status.status !== 'failed'
  const rendering = status?.stage === 'rendering' || status?.source?.kind === 'created'
  const transcribed = status?.status === 'completed' || status?.stage === 'picking_clips'
  const processPct = status?.status === 'completed' ? 100 : Math.round((status?.progress || 0) * 100)
  const estimate = videoId ? estimates[videoId] : undefined
  const tutorial = tutorialUrl()

  return (
    <div className="min-w-0 space-y-6" data-testid="clips-tab">
      {error && (
        <div role="alert" className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <span className="min-w-0 break-words">{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-700 hover:text-red-900" aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="flex gap-1 rounded-lg bg-white p-1 shadow sm:inline-flex" role="group" aria-label="Mode">
        <button type="button" aria-pressed={mode === 'clips'} onClick={() => setMode('clips')} className={modeClass(mode === 'clips')}>
          Make clips
        </button>
        <button type="button" aria-pressed={mode === 'create'} onClick={() => setMode('create')} className={modeClass(mode === 'create')}>
          Create a video
        </button>
      </div>

      {callClips && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-900" data-testid="call-banner">
          <p>
            <span className="font-medium">
              Your {callClips.call.coin} {callClips.call.direction}{' '}
              {callClips.call.status === 'closed' ? `closed: ${callClips.call.result_text}.` : 'is still open.'}
            </span>{' '}
            {callClips.clips.length
              ? `This clip in ${callClips.clips[0].filename} is where you called it, with its receipt attached.` +
                (callClips.clips.length > 1 ? ` ${callClips.clips.length} clips mention it.` : '')
              : 'None of your videos mentions this call, so here it is as a video of its own.'}
          </p>
          <button type="button" onClick={closeCall} className="shrink-0 text-primary-700 hover:underline">
            Close
          </button>
        </div>
      )}

      {mode === 'create' ? (
        <CreateVideo
          config={config}
          initialCallId={callClips && !callClips.clips.length ? callClips.call.id : null}
          onStarted={(job) => {
            setMode('clips')
            openVideo(job.file_id)
            loadVideos()
          }}
        />
      ) : (
        <>
          {/* Top card: the open video, an upload in progress, or the drop zone. */}
          <div className="bg-white rounded-lg shadow p-5">
            {upload ? (
              <div data-testid="upload-progress">
                <p className="truncate font-medium text-gray-900">{upload.filename}</p>
                <div className="mt-3 h-2 w-full overflow-hidden rounded bg-gray-200">
                  <div className="h-full bg-primary-600 transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  {upload.progress.retrying
                    ? 'Retrying… nothing already sent is lost.'
                    : `${formatBytes(upload.progress.sent)} of ${formatBytes(upload.progress.total)} · ${uploadPct}%` +
                      (upload.progress.bytesPerSecond > 0 ? ` · ${formatBytes(upload.progress.bytesPerSecond)}/s` : '') +
                      (secondsLeft !== null ? ` · ${formatTime(secondsLeft)} left` : '')}
                </p>
                {uploadEstimate !== null && (
                  <p className="mt-1 text-xs text-gray-500" data-testid="processing-estimate">
                    After the upload, processing takes {formatEstimate(uploadEstimate)} (estimate).
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-4">
                  <Step label="Uploaded" done={false} active />
                  <Step label="Transcribed" done={false} active={false} />
                  <Step label="Clips picked" done={false} active={false} />
                </div>
              </div>
            ) : videoId && status ? (
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="h-24 w-40 shrink-0 overflow-hidden rounded-md bg-black">
                  {mediaUrl && results?.has_video !== false && (
                    <video src={`${mediaUrl}#t=1`} preload="metadata" muted className="h-full w-full object-cover" aria-label="Video thumbnail" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <h3 className="min-w-0 truncate text-lg font-semibold text-gray-900">
                      {results?.filename || status.filename || 'Video'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => openVideo(null)}
                      className="shrink-0 rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Upload another
                    </button>
                  </div>
                  <p className="text-sm text-gray-500">
                    {results
                      ? `${formatTime(duration)} · ${created ? 'made' : 'uploaded'} ${formatDate(results.created_at)}`
                      : rendering
                        ? status.status === 'failed'
                          ? `Stopped at ${processPct}%`
                          : `Rendering… ${processPct}%`
                        : status.status === 'failed'
                          ? 'Stopped'
                          : 'Processing…'}
                  </p>
                  {processing && !rendering && estimate !== undefined && (
                    <p className="text-xs text-gray-500" data-testid="processing-estimate">
                      Usually takes {formatEstimate(estimate)} for a video this long (estimate).
                    </p>
                  )}
                  <div className="mt-3 h-2 w-full overflow-hidden rounded bg-gray-200">
                    <div
                      className={`h-full transition-all ${status.status === 'failed' ? 'bg-danger-500' : 'bg-primary-600'}`}
                      style={{ width: `${status.status === 'failed' ? 100 : processPct}%` }}
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-4">
                    {rendering ? (
                      <Step label="Rendered" done={status.status === 'completed'} active={!!processing} />
                    ) : (
                      <>
                        <Step label="Uploaded" done active={false} />
                        <Step label="Transcribed" done={transcribed} active={!!processing && !transcribed} />
                        <Step label="Clips picked" done={status.status === 'completed'} active={status.stage === 'picking_clips'} />
                      </>
                    )}
                  </div>
                  {status.status === 'failed' && (
                    <p role="alert" className="mt-2 text-sm text-danger-600">
                      {rendering ? 'Rendering' : 'Processing'} failed: {status.error || 'unknown error'}
                    </p>
                  )}
                </div>
              </div>
            ) : videoId ? (
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-gray-500">Loading the video…</p>
                <button
                  type="button"
                  onClick={() => openVideo(null)}
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Upload another
                </button>
              </div>
            ) : (
              <>
                <ol className="mb-4 grid gap-3 sm:grid-cols-3" aria-label="How it works">
                  {HOW_IT_WORKS.map((step, i) => (
                    <li key={step.title} className="flex gap-3 rounded-md bg-gray-50 p-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
                        {i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900">{step.title}</span>
                        <span className="block text-xs text-gray-500">{step.text}</span>
                      </span>
                    </li>
                  ))}
                </ol>
                {tutorial && (
                  <a href={tutorial} target="_blank" rel="noopener noreferrer" className="mb-4 inline-block text-sm font-medium text-primary-600 underline">
                    Watch the tutorial
                  </a>
                )}
                <div
                  onDragOver={(e) => {
                    e.preventDefault()
                    setDragging(true)
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  onClick={() => fileInput.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInput.current?.click()}
                  className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center sm:p-10 ${
                    dragging ? 'border-primary-600 bg-primary-50' : 'border-gray-300'
                  }`}
                  data-testid="upload-dropzone"
                >
                  <p className="font-medium text-gray-900">Drop a video here, or click to choose one</p>
                  <p className="mt-1 text-sm text-gray-500">MP4, MOV, M4V, MKV, WebM or AVI. Big files are fine: the upload resumes if it's interrupted.</p>
                  <input
                    ref={fileInput}
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    data-testid="upload-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      e.target.value = ''
                      if (file) startUpload(file)
                    }}
                  />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                  <span>No video yet?</span>
                  <button
                    type="button"
                    onClick={() => setRecording(true)}
                    className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Record
                  </button>
                </div>
              </>
            )}
          </div>

          {results && (
            <div className="grid min-w-0 gap-6 lg:grid-cols-[1fr_300px]">
              <section className="min-w-0 bg-white rounded-lg shadow p-5" aria-label="Suggested clips">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">{created ? 'Your video' : 'Suggested clips'}</h3>
                  {results.clips?.picker === 'heuristic' && !created && (
                    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">Quick pick (not AI)</span>
                  )}
                </div>
                {clips.length > 0 && !created && (
                  <p className="mt-1 text-xs text-gray-500" data-testid="clip-score-meaning">
                    {scoreMeaning(results.clips?.picker)}
                  </p>
                )}
                {!created && results.status === 'completed' && (
                  <FindClips
                    videoId={results.file_id}
                    aiAvailable={!!config?.ai_available}
                    onFound={(r) => setResults((prev) => (prev ? { ...prev, clips: r.clips, post_copy: r.post_copy } : prev))}
                  />
                )}
                {results.clips?.error && <p className="mt-2 text-sm text-danger-600">{results.clips.error}</p>}
                {clips.length === 0 && !results.clips?.error && (
                  <p className="mt-2 text-sm text-gray-500">No clips were found in this video.</p>
                )}
                <ul className="mt-3 divide-y divide-gray-100">
                  {clips.map((clip) => {
                    const key = clipKey(clip)
                    const range = rangeOf(clip)
                    const isSelected = key === selectedKey
                    const match = matches?.clips?.[key]
                    return (
                      <li
                        key={key}
                        onClick={() => setSelectedKey(key)}
                        className={`cursor-pointer py-3 ${isSelected ? 'bg-gray-50' : ''}`}
                        data-testid="clip-row"
                        aria-current={isSelected || undefined}
                      >
                        <div className="flex items-center gap-3">
                          <ScoreRing score={clip.score} />
                          <button type="button" onClick={() => setSelectedKey(key)} className="min-w-0 flex-1 text-left">
                            <p className="truncate font-medium text-gray-900">{clip.title}</p>
                            <p className="truncate text-sm text-gray-500">{clip.reason}</p>
                            <p className="text-xs text-gray-500">
                              {formatTime(range.start)} – {formatTime(range.end)}
                              {trims[key] ? ' · trimmed' : ''}
                            </p>
                            {clip.found_for && (
                              <p className="text-xs text-primary-700" data-testid="clip-found-for">
                                Found for: {clip.found_for}
                              </p>
                            )}
                            {clip.edges && !trims[key] && (
                              <p className="text-xs text-gray-500" data-testid="clip-edges">
                                Cut like your past clips: {edgesSentence(clip.edges)}
                              </p>
                            )}
                          </button>
                          {!created && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedKey(key)
                                setTrimmingKey(trimmingKey === key ? null : key)
                              }}
                              aria-expanded={trimmingKey === key}
                              className="shrink-0 rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Trim
                            </button>
                          )}
                        </div>
                        {(key === bestKey || !!match?.matches.length || !!match?.no_receipt) && (
                          <div className="mt-2 flex flex-wrap gap-2 pl-[3.75rem]">
                            {key === bestKey && (
                              <span className="rounded-full bg-success-600 px-2 py-0.5 text-xs font-medium text-white">Best clip</span>
                            )}
                            {match?.matches.map((m) => (
                              <span key={m.id} className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-600">
                                matches call {m.receipt_path}
                              </span>
                            ))}
                            {match?.no_receipt && !match.matches.length && (
                              <span
                                className="text-xs text-gray-500"
                                title="This clip mentions a trade, but none of your locked calls has that coin and direction."
                              >
                                No receipt
                              </span>
                            )}
                          </div>
                        )}
                        {trimmingKey === key && !created && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <TrimEditor
                              range={range}
                              suggested={{ start: clip.start, end: clip.end }}
                              duration={duration}
                              saving={savingKeys.has(key)}
                              words={words[key] ?? null}
                              onChange={(r) => changeTrim(clip, r)}
                              onReset={() => resetTrim(clip)}
                              onPlay={() => preview.current?.play(rangeOf(clip))}
                            />
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </section>

              <ClipPreview
                ref={preview}
                mediaUrl={mediaUrl}
                title={selected?.title || null}
                range={selected ? rangeOf(selected) : null}
                postCopy={selected ? postCopyOf(selected) : undefined}
                created={created}
                options={options}
                onOptions={(change) => setOptions((prev) => ({ ...prev, ...change }))}
                hookText={selected ? hookTextOf(selected) : ''}
                onHookText={(text) => selected && setHookTexts((prev) => ({ ...prev, [clipKey(selected)]: text }))}
                matches={selected && !created ? matchesOf(selected) : []}
                confirmedCallId={selected ? confirmedCalls[clipKey(selected)] ?? null : null}
                onConfirmCall={(callId) => {
                  if (!selected) return
                  const key = clipKey(selected)
                  setConfirmedCalls((prev) => ({ ...prev, [key]: callId }))
                  if (callId === null) setReceipts((prev) => ({ ...prev, [key]: false }))
                }}
                receiptOn={selected ? !!receipts[clipKey(selected)] : false}
                onReceipt={(on) => selected && setReceipts((prev) => ({ ...prev, [clipKey(selected)]: on }))}
                onExport={() => selected && startExport(selected)}
                exportView={selectedView}
                downloadUrl={downloadUrl}
                captionUrls={captionUrls}
                overlay={overlay}
                receiptLines={selected && !created ? receiptLinesOf(selected) : null}
                loadCrop={results ? (start, end) => clipFactoryAPI.cropPath(results.file_id, start, end) : undefined}
                fixPanel={
                  selected && results && !created ? (
                    <FixPanel
                      videoId={results.file_id}
                      clipKey={clipKey(selected)}
                      range={rangeOf(selected)}
                      postCopy={postCopyOf(selected)}
                      aiAvailable={!!config?.ai_available}
                      tradeTalk={!!selected.trade_talk}
                      onCaptionsChanged={captionsChanged}
                      onPostCopy={(copy) => postCopySaved(selected, copy)}
                    />
                  ) : null
                }
                post={
                  selected && results
                    ? {
                        videoId: results.file_id,
                        filename: results.filename,
                        clipKey: clipKey(selected),
                        title: selected.title,
                        range: rangeOf(selected),
                      }
                    : null
                }
              />
            </div>
          )}

          {results && !created && results.status === 'completed' && config?.features?.episode_package && (
            <EpisodePackage
              videoId={results.file_id}
              filename={results.filename}
              pkg={results.package || null}
              aiAvailable={!!config.ai_available}
              txtUrl={results.package ? signedUrl(results.file_id, 'package.txt', token) : null}
              onPackage={(pkg) => setResults((prev) => (prev ? { ...prev, package: pkg } : prev))}
              onPlay={(start, end) => preview.current?.play({ start, end })}
              duration={duration}
            />
          )}

          <ClipHistoryPanel />

          <section className="bg-white rounded-lg shadow p-5" aria-label="Your videos">
            <h3 className="text-lg font-semibold text-gray-900">Your videos</h3>
            {videos === null ? (
              <p className="mt-2 text-sm text-gray-500">Loading…</p>
            ) : videos.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">No videos yet.</p>
            ) : (
              <ul className="mt-2 divide-y divide-gray-100">
                {videos.map((v) => (
                  <li key={v.file_id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{v.filename}</p>
                      <p className="text-xs text-gray-500">
                        {formatDate(v.created_at)}
                        {v.duration ? ` · ${formatTime(v.duration)}` : ''}
                        {v.status === 'completed' ? ` · ${v.clip_count} clips` : ` · ${v.status}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => openVideo(v.file_id)}
                        disabled={v.file_id === videoId}
                        aria-label={`Open ${v.filename}`}
                        className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(v)}
                        aria-label={`Delete ${v.filename}`}
                        className="rounded-md border border-gray-300 px-3 py-1 text-sm text-danger-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <RecordDialog
        isOpen={recording}
        onClose={() => setRecording(false)}
        onRecorded={(file, seconds) => {
          setRecording(false)
          startUpload(file, seconds)
        }}
      />

      <ConfirmDialog
        isOpen={!!deleting}
        title="Delete this video?"
        message={
          <>
            <strong>{deleting?.filename}</strong> and its transcript, clips, trims and exports will be deleted from Clip
            Factory. This can't be undone.
          </>
        }
        confirmLabel="Delete"
        danger
        confirming={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  )
}

export default ClipsTab
